import { CommonModule, CurrencyPipe } from '@angular/common';
import {
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import * as countryJson from '@assets/data/country-code.json';
import * as subCountryJson from '@assets/data/sub-country-code.json';
import { TimerComponent } from '@core/components/timer/timer.component';
import { ModalsService } from '@core/modals/modals.service';
import {
  BraintreePayload,
  Configuration,
  CountryList,
  PatronAddressModel,
  PatronModel,
  SubCountryItem,
  SubCountryObject,
  TransactionModel,
  TransactionWrapper,
} from '@core/models';
import { AdobeDataPages } from '@core/models/adobe-data-layer.model';
import { OktaIntegrationModule } from '@core/okta-integration/okta-integration.module';
import {
  ApiException,
  CartService,
  ConfigurationService,
} from '@core/services';
import { AdobeDataLayerService } from '@core/services/adobe-data-layer-service.service';
import { TransactionService } from '@core/services/transaction.service';
import { environment } from '@environments/environment';
import { UserClaims } from '@okta/okta-auth-js';
import { Subscription } from 'rxjs';
import { CheckoutRecapComponent } from './components/checkout-recap/checkout-recap.component';
import { ConfirmationPageComponent } from './components/confirmation-page/confirmation-page.component';
import { PatronComponent } from './components/patron/patron.component';
import { PaymentDetails } from './models/payment-details.model';
import { ConnectionService } from './services/connection.service';

declare var braintree: any;

type ComponentPaymentDetails = Omit<
  PaymentDetails,
  'paymentMethod' | 'cardType' | 'firstName' | 'lastName' | 'email' | 'address'
> & {
  paymentMethod: string | null;
  cardType: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  address:
    | (Omit<PatronAddressModel, 'city' | 'postalCode' | 'address1'> & {
        city: string | null;
        postalCode: string | null;
        address1: string | null;
      })
    | null;
  phone: {
    phone: string;
    countryCode: string;
  } | null;
};

@Component({
  imports: [
    CommonModule,
    ConfirmationPageComponent,
    FormsModule,
    OktaIntegrationModule,
    PatronComponent,
    TimerComponent,
    CheckoutRecapComponent,
  ],
  providers: [CurrencyPipe],
  selector: 'app-payment',
  standalone: true,
  styleUrls: ['./payment.component.scss'],
  templateUrl: './payment.component.html',
})
export class PaymentComponent implements OnInit, OnDestroy {
  private activatedRoute = inject(ActivatedRoute);
  private adobeDataLayerService = inject(AdobeDataLayerService);
  private cartService = inject(CartService);
  private configurationService = inject(ConfigurationService);
  private connectionService = inject(ConnectionService);
  private modalsService = inject(ModalsService);
  private router = inject(Router);
  private transactionService = inject(TransactionService);

  @ViewChild('paymentForm') form: NgForm;
  patron: PatronModel;
  apiUrl = environment.apiUrl;
  transactionId: string;
  data: { transaction: TransactionModel | null; btToken: string } = {
    transaction: null,
    btToken: '',
  };
  countryList: CountryList = [];
  subCountryObject: SubCountryObject = {};
  subCountryList: SubCountryItem[] = [];
  paymentDetails: ComponentPaymentDetails = {
    nonce: null,
    paymentMethod: null,
    cardType: null,
    acceptedTerms: false,
    firstName: null,
    lastName: null,
    email: null,
    digitalWallet: null,
    accountCredit: {
      credits: 0,
      payFull: false,
    },
    address: null,
    phone: null,
  };
  subscriptions: Subscription[] = [];
  displayCredits: string = '0.00';
  confirmationPage: boolean;
  transaction = signal<TransactionWrapper | null>(null);
  public isTicketFeeEnabled = signal(false);

  constructor(private currencyPipe: CurrencyPipe) {}

  ngOnInit(): void {
    this.confirmationPage = false;
    const transaction = this.activatedRoute.snapshot.data[
      'transaction'
    ] as TransactionWrapper;
    this.transactionService.transaction$.next(transaction);
    this.transactionId = this.activatedRoute.snapshot.params['id'];
    this.connectionService.token =
      this.activatedRoute.snapshot.queryParams['token'];

    this.paymentDetails.paymentMethod =
      this.configurationService.configuration.paymentMethod;

    this.getIsClientTicketFeeEnabled();

    this.initializeBraintree(transaction);
    this.initializeComponent();
    this.adobeDataLayerService.pageEventSubject.subscribe(async page => {
      if (page !== AdobeDataPages.Payment) {
        return;
      }

      const ticket_addons = [
        {
          name: this.transactionService.getTransactionUpsell()?.name || '',
          price:
            this.transactionService.getTransactionUpsell()?.price?.toString() ||
            '0',
        },
      ];

      if (this.transactionService.hasRainoutInsurance()) {
        ticket_addons.push({
          name: 'rainout insurance',
          price: this.transactionService.getInsurancePrice()?.toString(),
        });
      }
      const gamePk = await this.adobeDataLayerService.getGamePK();
      const adobePayload = {
        event: 'begin-checkout',
        ticketing: {
          events: [
            {
              event_id: this.adobeDataLayerService.getEventId(),
              matchup_full: this.adobeDataLayerService.getMatchupFull(),
              game_pk: gamePk,
              ticket_quantity: this.transactionService
                .getTotalSeats()
                ?.toString(),
              ticket_total_price: this.transactionService
                .getTotalPrice()
                ?.toString(),
              ticket_unit_price: this.transactionService
                .getUnitPrice()
                ?.toString(),
              ticket_seat_section:
                this.cartService.getSeatsSections()?.[0] || '',
              ticket_addons,
            },
          ],
        },
      };

      this.adobeDataLayerService.push(adobePayload);
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(o => o.unsubscribe());
  }

  get configuration() {
    return this.configurationService.configuration;
  }

  get substractCreditsToBalance(): number {
    const balance =
      this.transaction()?.transaction.tdcTransaction?.balance || 0;
    const result = balance - this.paymentDetails.accountCredit.credits;
    return result > 0 ? result : 0;
  }

  get showCreditsApplies(): number {
    const credits = this.paymentDetails.accountCredit.credits;
    return credits && credits > 0 ? credits : 0;
  }

  initializeBraintree(transaction: TransactionWrapper) {
    if (!transaction) {
      const modalData = {
        title: 'ERROR',
        content: 'An Error occurred while trying to get the Transaction.',
        acceptBtnName: 'CLOSE',
        acceptFunction: this.onTransactionExpiredAction.bind(this),
      };
      this.modalsService.openModal(modalData);
      return;
    }

    this.data = transaction;
    this.transaction.set({ ...transaction });

    setTimeout(() => {
      const button = document.querySelector('#braintree-button');
      braintree.dropin.create(
        {
          authorization: this.data.btToken,
          selector: '#dropin-container',
        },
        (err: any, instance: any) => {
          if (button) {
            button.addEventListener('click', () => {
              instance.requestPaymentMethod(
                (_err: any, payload: BraintreePayload) => {
                  this.paymentDetails.nonce = payload.nonce;
                  this.paymentDetails.cardType =
                    payload.details.cardType.toLowerCase();
                }
              );
            });
          }
        }
      );
    }, 300);
  }

  initializeComponent() {
    this.countryList = (countryJson as any)['default'];
    this.subCountryObject = (subCountryJson as any)['default'];
    if (this.configurationService.configuration.extraCheckbox)
      this.paymentDetails['extraCheckbox'] = false;
    if (this.configuration.custom.paymentPage.captureAddress) {
      this.paymentDetails.address = {
        subCountryCode: '',
        city: null,
        countryCode: 'USA',
        postalCode: null,
        address1: null,
      };
      this.subCountryList =
        this.subCountryObject[this.paymentDetails.address.countryCode];
    }
    if (this.configuration.custom.paymentPage.capturePhone) {
      this.paymentDetails.phone = {
        phone: '',
        countryCode: 'USA',
      };
    }
  }

  async checkout() {
    const configuration = this.configurationService.configuration;
    const balance =
      this.transaction()?.transaction?.tdcTransaction?.balance || 0;
    this.paymentDetails.paymentMethod =
      this.configurationService.configuration.paymentMethod;
    const { credits, payFull } = this.paymentDetails.accountCredit;
    if (
      !this.paymentDetails.nonce &&
      !this.paymentDetails.digitalWallet &&
      !payFull
    ) {
      const content = this.patron
        ? 'Please enter valid credit cart, select a digital wallet or pay all with credit. '
        : 'Please enter valid credit card.';
      const modalData = {
        title: 'Payment information',
        content: content,

        acceptBtnName: 'CLOSE',
      };
      this.modalsService.openModal(modalData);
      return;
    }

    if (credits > balance) {
      const modalData = {
        title: 'Account Credit',
        content: 'The credit must be less than or equal to the balance.',

        acceptBtnName: 'CLOSE',
      };
      this.modalsService.openModal(modalData);
      return;
    }
    if (!this.paymentDetails.acceptedTerms) {
      const modalData = {
        title: 'Accept terms',
        content: 'Please accept the terms and conditions.',

        acceptBtnName: 'CLOSE',
      };
      this.modalsService.openModal(modalData);
      return;
    }
    const extraCheckboxIsRequiredAndActive =
      configuration.extraCheckbox &&
      configuration.extraCheckbox.active &&
      configuration.extraCheckbox.required;
    if (
      !this.paymentDetails.extraCheckbox &&
      extraCheckboxIsRequiredAndActive
    ) {
      const modalData = {
        title: 'Accept Broker Polcy',
        content: 'Please accept the Brokers Policy.',

        acceptBtnName: 'CLOSE',
      };
      this.modalsService.openModal(modalData);
      return;
    }

    if (
      !this.paymentDetails.firstName ||
      !this.paymentDetails.lastName ||
      !this.paymentDetails.email
    ) {
      const modalData = {
        title: 'Required fields',
        content: 'You have to enter first name, last name and email.',

        acceptBtnName: 'CLOSE',
      };
      this.modalsService.openModal(modalData);
      return;
    }
    if (this.form.form.controls['email'].invalid) {
      const modalData = {
        title: 'Valid email',
        content: 'Please enter a valid email.',

        acceptBtnName: 'CLOSE',
      };
      this.modalsService.openModal(modalData);
      return;
    }

    if (this.checkAddressInputs(configuration)) {
      const modalData = {
        title: 'Required fields',
        content: 'You must enter your address data',

        acceptBtnName: 'CLOSE',
      };
      this.modalsService.openModal(modalData);
      return;
    }

    if (this.checkPhoneInputs(configuration.custom.paymentPage.capturePhone)) {
      const modalData = {
        title: 'Required fields',
        content: 'You must enter your phone data',

        acceptBtnName: 'CLOSE',
      };
      this.modalsService.openModal(modalData);
      return;
    }

    const subscription = this.connectionService
      .checkoutTransaction(
        this.transactionId,
        this.paymentDetails as PaymentDetails
      )
      .subscribe({
        next: () => {
          this.confirmationPage = true;
          setTimeout(() => {
            this.confirmationPage = false;
            this.router.navigate(['', 'summary', this.transactionId], {
              queryParams: { token: this.connectionService.token },
            });
          }, 4000);
        },

        error: error => {
          const modalData = {
            title: 'ERROR',
            content:
              'An Error occurred while trying to checkout the Transaction.',
            acceptBtnName: 'CLOSE',
          };

          if (error instanceof ApiException) {
            modalData.content = error.message;
          }
          this.modalsService.openModal(modalData);
        },
      });

    this.subscriptions.push(subscription);
  }

  /**
   * Should check phone inputs if is needed
   * @since 1.4.0
   * @version 1.4.0
   * @param shouldCapturePhone should i capture phone
   * @returns all is good
   */
  private checkPhoneInputs(shouldCapturePhone: boolean): boolean {
    const checkPhoneForm = shouldCapturePhone && !this.patron;
    const phoneAreValid =
      !this.paymentDetails.phone?.phone ||
      !this.paymentDetails.phone?.countryCode;
    return checkPhoneForm && phoneAreValid;
  }

  /**
   * Only check address inputs if capture address are activate and patron dont exist
   * @param configuration Club Configuration
   * @returns {boolean}
   * @version 1.3.1
   * @since 1.3.1
   */
  private checkAddressInputs(configuration: Configuration): boolean {
    const checkAddressForm =
      configuration.custom.paymentPage.captureAddress && !this.patron;

    const addressInputsAreValid =
      !this.paymentDetails.address?.address1 ||
      !this.paymentDetails.address?.city ||
      !this.paymentDetails.address?.countryCode ||
      !this.paymentDetails.address?.postalCode ||
      !this.paymentDetails.address?.subCountryCode;

    return checkAddressForm && addressInputsAreValid;
  }

  showErrorModal(message: string) {
    const modalData = {
      title: 'Payment information',
      content: message,
      acceptBtnName: 'CLOSE',
    };
    this.modalsService.openModal(modalData);
    return;
  }

  goCheckout() {
    this.router.navigate([`checkout/${this.transactionId}`], {
      queryParams: { token: this.connectionService.token },
    });
  }

  onTransactionExpired() {
    const modalData = {
      title: 'Transaction expired',
      content: 'The transaction has expired, please try again.',
      acceptBtnName: 'CLOSE',
      acceptFunction: this.onTransactionExpiredAction.bind(this),
    };
    this.modalsService.openModal(modalData);
  }

  goSeatSelection() {
    let eventId = 10170;
    const transaction = this.transaction();

    if (transaction) {
      eventId = (transaction.transaction as Required<TransactionModel>).event
        .pvEventId;
    }
    this.router.navigate(['seat-selection'], {
      queryParams: { event: eventId },
    });
  }

  onTransactionExpiredAction() {
    const subscription = this.connectionService
      .deleteTransaction(this.transactionId)
      .subscribe({
        next: response => {
          this.goSeatSelection();
        },
        error: error => {
          this.goSeatSelection();
        },
      });
    this.subscriptions.push(subscription);
  }

  infoEmailModal() {
    const clientName = this.configurationService.configuration.clientName;
    const modalData = {
      title: 'MLB Ballpark App',
      content: `${clientName} tickets are available exclusively as mobile tickets via the free MLB Ballpark app. To view and use tickets, log in to your existing MLB.com account or create an account in the MLB Ballpark app with the email used to purchase your ticket(s). Detailed instructions on using the MLB Ballpark app, including email verification, viewing tickets, and forwarding tickets, are available at <a href="${this.configurationService.configuration.clientBallParkUrl}" target="_blank">${this.configurationService.configuration.clientBallParkText}</a>.`,
      acceptBtnName: 'CLOSE',
    };
    this.modalsService.openModal(modalData);
  }

  oktaEventEmitter(instance: UserClaims): void {
    let patron = instance;
    const subscription = this.connectionService
      .postAssociatePatronToCart(
        patron.sub,
        patron.email as string,
        parseInt(this.transactionId, 10),
        this.connectionService.token
      )
      .subscribe({
        next: response => {
          this.patron = response.data as PatronModel;
          this.paymentDetails.firstName = this.patron.firstName;
          this.paymentDetails.lastName = this.patron.lastName;
          this.paymentDetails.email = this.patron.email;
          this.paymentDetails.address = this.patron.address;
        },
        error: error => {
          const modalData = {
            title: 'ERROR',
            content:
              'The MLB account used does not exists for this club entity. Please try again with another account.',
            acceptBtnName: 'CLOSE',
            acceptFunction: () => undefined,
          };

          if (error instanceof ApiException) {
            modalData.content = error.message;

            if (!error.data?.['isPatronNotFounError']) {
              modalData['acceptFunction'] = () => {
                this.restartTransaction();
              };
            }
          }
          this.modalsService.openModal(modalData);
        },
      });
    this.subscriptions.push(subscription);
  }

  onChangeDigitalWalletSelector(): void {
    if (!this.paymentDetails.digitalWallet) {
      return;
    }
    this.paymentDetails.cardType =
      this.paymentDetails.digitalWallet.cardType.description || '';
  }

  checkboxPayInFullChange(instance: Event): void {
    const isChecked = <HTMLInputElement>instance.currentTarget;
    const balance =
      this.transaction()!.transaction.tdcTransaction?.balance || 0;
    const patronAccountCredit = this.patron?.accountCredit || 0;

    if (isChecked && patronAccountCredit >= balance) {
      this.paymentDetails.accountCredit.credits = balance;
    } else if (isChecked && patronAccountCredit <= balance) {
      this.paymentDetails.accountCredit.credits = patronAccountCredit;
    } else if (!isChecked) {
      this.paymentDetails.accountCredit.credits = 0;
    }

    this.formatForDisplay();
  }

  private restartTransaction() {
    const subscription = this.connectionService
      .deleteTransaction(this.transactionId)
      .subscribe({
        next: response => {
          this.goSeatSelection();
        },
        error: error => {
          this.goSeatSelection();
        },
      });
    this.subscriptions.push(subscription);
  }

  onBlurInputCredits() {
    const formattedValue = this.currencyPipe.transform(
      this.paymentDetails.accountCredit.credits,
      'USD',
      'symbol',
      '1.2-2'
    ) as string;
    const patronAccountCredit = this.patron?.accountCredit || 0;

    this.paymentDetails.accountCredit.credits = +formattedValue;

    if (+this.paymentDetails.accountCredit.credits > patronAccountCredit) {
      this.paymentDetails.accountCredit.credits = +patronAccountCredit;
    }
  }

  onCreditsChange(value: string) {
    const numericValue = parseFloat(value.replace(/,/g, ''));

    this.paymentDetails.accountCredit.credits = isNaN(numericValue)
      ? 0
      : numericValue;

    this.displayCredits = value;
  }

  formatForDisplay() {
    // Format the number with comma separators and two decimal places for display
    this.displayCredits =
      this.paymentDetails.accountCredit.credits.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
  }

  onChangeCountry(event: string) {
    this.subCountryList = this.subCountryObject[event];
  }

  private getIsClientTicketFeeEnabled() {
    const getCurrentClient$ = this.connectionService
      .getCurrentClient()
      .subscribe(response => {
        this.isTicketFeeEnabled.set(!!response.data?.isTicketFeeEnabled);
      });

    this.subscriptions.push(getCurrentClient$);
  }
}
